import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fmtMoney, fmtTime, fmtDate, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";
import TruckInspectionDialog from "@/components/TruckInspectionDialog";
import { CheckCircle2, ShieldAlert, ClipboardCheck } from "lucide-react";

const PAY_METHODS = [
  { value: "hourly", label: "Hourly only" },
  { value: "commission", label: "Commission only" },
  { value: "higher_of", label: "Higher of hourly or commission" },
  { value: "hourly_plus_commission", label: "Hourly + commission" },
];

export default function FinalizeDay() {
  const [searchParams] = useSearchParams();
  const targetDate = searchParams.get("date"); // back-finalize a specific past day
  const [summary, setSummary] = useState(null);
  const [payMethod, setPayMethod] = useState("higher_of");
  const [certified, setCertified] = useState(false);
  const [signature, setSignature] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [inspectionOpen, setInspectionOpen] = useState(false);
  const navigate = useNavigate();

  const load = async (method) => {
    const params = {};
    if (method) params.pay_method = method;
    if (targetDate) params.date = targetDate;
    const { data } = await api.get("/daily/summary", { params });
    setSummary(data);
    if (!method) setPayMethod(data.pay_method);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [targetDate]);
  useEffect(() => { if (summary) load(payMethod); /* eslint-disable-next-line */ }, [payMethod]);

  const submit = async () => {
    if (!certified || !signature) {
      toast.error("Please certify and sign before submitting");
      return;
    }
    setBusy(true);
    try {
      const body = {
        pay_method: payMethod,
        signature_data: signature,
        certified: true,
        employee_notes: notes,
      };
      if (targetDate) body.date = targetDate;
      await api.post("/daily/finalize", body);
      toast.success("Day submitted for admin approval");
      navigate(targetDate ? "/week" : "/");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!summary) return <div className="py-20 text-center text-slate-500">Loading…</div>;

  const t = summary.time_entry || {};
  const l = summary.lunch_entry || {};
  const submitted = !!summary.submission;
  const ready = !!(t.clock_in && t.clock_out);
  const inspectionMissing = !!summary.inspection_required_today && !summary.truck_inspection;

  return (
    <div className="space-y-5" data-testid="finalize-page">
      <header>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">
          {targetDate ? `Finalize ${fmtDate(targetDate)}` : "Finalize Day"}
        </p>
        <h1 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900 mt-1">Review &amp; Submit</h1>
        {targetDate && (
          <p className="text-xs text-amber-700 mt-1" data-testid="finalize-backdated-note">
            You're finalizing a past day. Make sure all jobs and times are correct before submitting.
          </p>
        )}
      </header>

      {!ready && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
          You must clock in and clock out before you can submit your day.
        </div>
      )}

      {submitted && (
        <div className="rounded-xl bg-sky-50 border border-sky-200 p-3 text-sm text-sky-900">
          This day has been submitted ({summary?.submission?.status || "pending"}).
        </div>
      )}

      {inspectionMissing && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-900 flex items-start gap-3" data-testid="inspection-block-banner">
          <ShieldAlert className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-bold">Truck pre-inspection required.</p>
            <p className="text-xs">You can't finalize your day until this is completed.</p>
          </div>
          <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => setInspectionOpen(true)} data-testid="open-inspection-from-finalize">
            <ClipboardCheck className="h-4 w-4 mr-1" /> Complete now
          </Button>
        </div>
      )}

      {summary?.truck_inspection && (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900 flex items-center gap-2" data-testid="inspection-done-banner">
          <CheckCircle2 className="h-4 w-4" />
          <span>
            Truck pre-inspection submitted ·{" "}
            <button onClick={() => setInspectionOpen(true)} className="underline font-semibold">View / re-submit</button>
          </span>
        </div>
      )}

      {/* Today's Take — hero card that surfaces the winning pay method BEFORE
          the employee scrolls through Production / Pay Method / Estimated
          Pay. Kills "wait how did you get to that number?" confusion. */}
      {ready && (
        <Card className="rounded-2xl border-sky-200 bg-sky-50" data-testid="finalize-todays-take">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-sky-700 font-bold">Today's Take</p>
                <p className="text-3xl font-extrabold font-display text-sky-900 tabular-nums mt-0.5" data-testid="finalize-final-pay">
                  {fmtMoney(summary.pay?.estimated_final_pay || 0)}
                </p>
                <p className="text-[11px] text-sky-700 mt-0.5">
                  {payMethod === "higher_of"
                    ? `Higher of hourly (${fmtMoney(summary.pay?.estimated_hourly_pay || 0)}) vs commission (${fmtMoney(summary.pay?.estimated_commission_pay || 0)})`
                    : payMethod === "hourly"
                    ? `Hourly (${Number(summary.hours?.net_hours || 0).toFixed(2)}h × ${fmtMoney(summary.pay?.hourly_rate || 0)})`
                    : payMethod === "commission"
                    ? `Commission (${summary.pay?.commission_percentage || 0}% of ${fmtMoney(summary.production?.net_commissionable_revenue || 0)})`
                    : "Hourly + commission combined"}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-widest text-sky-700 font-bold">Net Hours</p>
                <p className="text-lg font-extrabold font-display text-sky-900 tabular-nums mt-0.5">
                  {Number(summary.hours?.net_hours || 0).toFixed(2)}h
                </p>
              </div>
            </div>
            <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-[11px] text-amber-900">
              This is an <b>estimate</b> — tax withholdings and any tips/bonuses are calculated on the full weekly paystub, not per day.
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-2xl border-slate-200">
        <CardHeader className="pb-3"><CardTitle className="text-base font-bold font-display">Time Summary</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-3 text-sm">
          <Row label="Clock In" value={fmtTime(t.clock_in)} />
          <Row label="Clock Out" value={fmtTime(t.clock_out)} />
          <Row label="Lunch Start" value={fmtTime(l.lunch_start)} />
          <Row label="Lunch End" value={fmtTime(l.lunch_end)} />
          <Row label="Total Hours" value={`${summary.hours.total_hours.toFixed(2)} h`} />
          <Row label="Net Payable" value={`${summary.hours.net_hours.toFixed(2)} h`} highlight />
        </CardContent>
      </Card>

      {(summary.shop_time?.length > 0 || summary.shop_hours_approved > 0 || summary.shop_hours_pending > 0) && (
        <Card className="rounded-2xl border-slate-200" data-testid="finalize-shop-time-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-bold font-display flex items-center justify-between">
              <span>Shop / Other Time</span>
              <span className="text-xs font-normal text-slate-500">
                {Number(summary.shop_hours_approved || 0).toFixed(2)}h approved
                {summary.shop_hours_pending > 0 && (
                  <span className="text-amber-600 font-semibold"> · {Number(summary.shop_hours_pending).toFixed(2)}h pending</span>
                )}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {summary.shop_hours_pending > 0 && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                Your pending shop-time hours will be added to your day's pay once your admin approves them. You can still submit your day below.
              </div>
            )}
            {(summary.shop_time || []).map((s) => (
              <div key={s.id} className="flex items-center justify-between border-b border-slate-100 last:border-0 pb-2 last:pb-0">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{s.category} <span className="font-normal text-slate-500">· {Number(s.hours).toFixed(2)}h</span></p>
                  {s.notes && <p className="text-xs text-slate-500 truncate">{s.notes}</p>}
                </div>
                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                  s.status === "approved" ? "bg-green-100 text-green-700"
                  : s.status === "rejected" ? "bg-red-100 text-red-700"
                  : "bg-amber-100 text-amber-800"
                }`}>{s.status}</span>
              </div>
            ))}
            <Row label="Shop Pay" value={fmtMoney(summary.pay?.shop_pay)} bold />
          </CardContent>
        </Card>
      )}

      <Card className="rounded-2xl border-slate-200">
        <CardHeader className="pb-3"><CardTitle className="text-base font-bold font-display">Production Summary</CardTitle></CardHeader>
        <CardContent className="space-y-2 text-sm">
          <Row label="Gross Invoice Total" value={fmtMoney(summary.production.gross_invoice_total)} />
          <Row label="Allowable Deductions" value={fmtMoney(summary.production.allowable_deductions)} />
          <Row label="Net Commissionable" value={fmtMoney(summary.production.net_commissionable_revenue)} bold />
          <Row label="Commission %" value={`${summary.pay.commission_percentage || 0}%`} />
          <Row label="Estimated Commission" value={fmtMoney(summary.pay.estimated_commission_pay)} />
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-slate-200">
        <CardHeader className="pb-3"><CardTitle className="text-base font-bold font-display">Pay Method</CardTitle></CardHeader>
        <CardContent>
          <RadioGroup value={payMethod} onValueChange={setPayMethod} disabled={submitted}>
            {PAY_METHODS.map((m) => (
              <div key={m.value} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
                <RadioGroupItem value={m.value} id={`pm-${m.value}`} data-testid={`pay-method-${m.value}`} />
                <Label htmlFor={`pm-${m.value}`} className="text-sm font-medium cursor-pointer flex-1">{m.label}</Label>
              </div>
            ))}
          </RadioGroup>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-slate-200 bg-slate-900 text-white">
        <CardHeader className="pb-3"><CardTitle className="text-base font-bold font-display text-white">Estimated Pay</CardTitle></CardHeader>
        <CardContent className="space-y-2 text-sm text-slate-200">
          <Row dark label="Hourly Pay" value={fmtMoney(summary.pay.estimated_hourly_pay)} />
          <Row dark label="Overtime" value={fmtMoney(summary.pay.estimated_overtime_pay)} />
          <Row dark label="Commission" value={fmtMoney(summary.pay.estimated_commission_pay)} />
          {Number(summary.pay?.shop_pay || 0) > 0 && (
            <Row dark label="Shop Pay" value={fmtMoney(summary.pay.shop_pay)} />
          )}
          <div className="border-t border-slate-700 my-2" />
          <div className="flex items-baseline justify-between">
            <span className="text-sm uppercase tracking-widest text-slate-400">Final Pay</span>
            <span className="text-3xl font-extrabold font-display text-green-400">{fmtMoney(summary.pay.estimated_final_pay)}</span>
          </div>
        </CardContent>
      </Card>

      {!submitted && (
        <Card className="rounded-2xl border-slate-200">
          <CardHeader className="pb-3"><CardTitle className="text-base font-bold font-display">Certify &amp; Sign</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={certified} onCheckedChange={setCertified} data-testid="certify-checkbox" />
              <span className="text-sm text-slate-700">I certify that the information submitted for this day is accurate.</span>
            </label>
            <SignaturePad value={signature} onChange={setSignature} testId="employee-signature-pad" />
            <Button
              disabled={busy || !ready || !certified || !signature || inspectionMissing}
              onClick={submit}
              className="w-full h-14 bg-green-600 hover:bg-green-700 rounded-2xl text-base font-bold"
              data-testid="finalize-submit-btn"
            >
              <CheckCircle2 className="h-5 w-5 mr-2" /> {inspectionMissing ? "Complete inspection first" : "Submit for Approval"}
            </Button>
          </CardContent>
        </Card>
      )}

      <TruckInspectionDialog open={inspectionOpen} onOpenChange={setInspectionOpen} onSaved={() => load(payMethod)} date={targetDate || undefined} />
    </div>
  );
}

function Row({ label, value, bold, highlight, dark }) {
  return (
    <div className="flex items-center justify-between">
      <span className={dark ? "text-slate-400" : "text-slate-600"}>{label}</span>
      <span className={`${highlight ? "text-lg font-extrabold text-green-700" : bold ? "font-bold" : "font-medium"} ${dark ? "text-white" : "text-slate-900"}`}>{value}</span>
    </div>
  );
}
